/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Icon,
  InlineMessage,
  MenuInput,
  type MenuInputOption,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { ApiError, ApiResponse } from '../../hooks/useGetDataProvider';

import { FieldLabel } from '../../../../shared/components';
import { testIds, VALIDATION_TYPES } from '../../../../shared/constants';
import { useDebounce } from '../../../../shared/hooks';
import useInvertedLocalize from '../../../../shared/hooks/useInvertedLocalize';
import {
  type DataProvider,
  type FieldElement,
  type FormValues,
  type MultiSelectField,
  type PreviewDataProviderPayload,
} from '../../../../shared/types';
import { useDataProviderTracking } from '../../../../shared/utils/troubleshooting';
import { DATA_PROVIDER } from '../../../FormBuilder';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';
import { type EntityInitialData } from '../../data-store';
import {
  useDefaultValue,
  useDependencyValues,
  useGetDataProvider,
  useInfiniteScroll,
} from '../../hooks';
import {
  determineTriggerType,
  hasDependencies,
  prepareApiTrackingData,
} from '../../utils/dataProviderTrackingUtils';
import {
  applyValueMatchingAndUpdateSelected,
  areDependenciesSatisfied,
  buildDependencies,
  createLoadMoreOptions,
  createOptionsValueKey,
  getFieldPlaceholder,
  getFixedOptions,
  handleMultiSelectCascading,
  isOnlyContextualEntityDependencies,
  isPaginationEnabled,
  resetOptionsLoadingState,
  shouldLoadOptions,
  validateOptions,
  validateSelectedOptions,
} from '../../utils/dataProviderUtils';
import { processMultiSelectValues } from '../../utils/valueProcessing';
import {
  loadedSnapshotMatchesCurrentSelection,
  normalizeValuesToSearchWireFromLoaded,
  shouldAttachValuesToSearch,
} from '../../utils/valuesToSearch';

import styles from './PreviewFields.module.css';

interface MultiSelectProps {
  field: MultiSelectField;
  value?: MenuInputOption[] | string | string[] | null;
  urlSlug?: string;
  customFormUid?: string;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  dataProvider: DataProvider;
  onChange?: (selectedOptions: MenuInputOption[]) => void;
  onTouch?: () => void;
  canvasFields?: FieldElement[];
  dataProviders?: Record<string, DataProvider>;
  formValues?: FormValues;
  isPreview?: boolean;
  isModalForm?: boolean;
  initialData?: EntityInitialData | null;
  /** When true (public LiveForm), skip HTTP/SQL data provider API calls */
  isPublicForm?: boolean;
  /** Entity or credential id in edit flows — enables valuesToSearch on data-provider requests */
  entityId?: string | null;
  /** Map of fieldName → fieldType for type-aware dependency formatting (e.g. date fields). */
  fieldTypeMap?: Map<string, string>;
  /** Loaded entity field data for edit flows — blocks default auto-select when entity data exists.
   *  Passed as prop to avoid Redux dependency in NPM package consumers. */
  loadedFormElementsData?: Record<string, unknown> | null;
}

/**
 * MultiSelect component for rendering MultiSelect fields in form preview
 */
function MultiSelect({
  field,
  value,
  urlSlug,
  customFormUid,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
  dataProvider,
  onChange,
  onTouch,
  canvasFields,
  dataProviders,
  formValues = {},
  isPreview,
  isModalForm = false,
  initialData = null,
  isPublicForm = false,
  entityId = null,
  fieldTypeMap,
  loadedFormElementsData = null,
}: MultiSelectProps): ReactElement {
  const blockDefaultAutoSelect = useMemo(
    () =>
      Boolean(
        entityId &&
          !isPublicForm &&
          (loadedFormElementsData === null ||
            Object.prototype.hasOwnProperty.call(
              loadedFormElementsData,
              field.fieldName
            ))
      ),
    [entityId, isPublicForm, loadedFormElementsData, field.fieldName]
  );

  const [options, setOptions] = useState<MenuInputOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<MenuInputOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const l = useInvertedLocalize();

  const debouncedSearchText = useDebounce(searchText, 500);

  const dataProviderValuesToSearch = useMemo(() => {
    if (!entityId || isPublicForm) {
      return undefined;
    }

    const loadedSnapshot = loadedFormElementsData?.[field.fieldName];

    if (loadedSnapshot === undefined) {
      return undefined;
    }

    if (
      !loadedSnapshotMatchesCurrentSelection(
        loadedSnapshot,
        value,
        'multiselect'
      )
    ) {
      return undefined;
    }

    return normalizeValuesToSearchWireFromLoaded(loadedSnapshot);
  }, [entityId, isPublicForm, loadedFormElementsData, field.fieldName, value]);

  const dependenciesSatisfiedForProvider = useMemo(
    () =>
      isOnlyContextualEntityDependencies(dataProvider) ||
      areDependenciesSatisfied(dataProvider, formValues, initialData),
    [dataProvider, formValues, initialData]
  );

  const { mutate: getDataProviderPreview } = useGetDataProvider({
    isPreview,
    customFormUid,
  });
  // Troubleshooting tracking hook
  const {
    trackValueSelected,
    trackApiTriggered,
    trackApiSuccess,
    trackApiError,
  } = useDataProviderTracking();

  const { processedValue: defaultProcessedValue } = useDefaultValue({
    field,
    value,
    isLoading,
    options,
    onChange,
    setSelectedOptions,
    providerType: dataProvider.type,
    formValues,
    initialData,
    blockDefaultAutoSelect,
  });

  const {
    fieldName,
    label,
    placeholder,
    helperText,
    isReadOnly: staticReadOnly,
    infoTooltipText,
    validations,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isReadOnly = staticReadOnly || isReadOnlyProp || false;

  const processedValue = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return defaultProcessedValue;
    }

    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === 'object' &&
      'value' in value[0]
    ) {
      // Validate array elements to ensure they're valid MenuInputOption objects
      const validOptions = (value as MenuInputOption[]).filter(
        (option) =>
          option?.value !== undefined &&
          option.value !== null &&
          option.label !== undefined
      );

      return validOptions.length > 0 ? validOptions : null;
    }

    const processed = processMultiSelectValues(value, dataProvider);
    // Validate processed values to ensure they're valid MenuInputOption objects

    return processed.filter(
      (option) =>
        option?.value !== undefined &&
        option.value !== null &&
        option.label !== undefined
    );
  }, [value, dataProvider, defaultProcessedValue]);

  const paginationEnabled = useMemo(
    () => isPaginationEnabled(dataProvider),
    [dataProvider]
  );

  // FIXED provider: keep options / checkmarks aligned with `processedValue` when it
  // changes after initial load (e.g. entity `/load` → resetForm). The main load effect
  // omits `processedValue` from deps to avoid redundant HTTP calls; SingleSelect avoids
  // that gap with `useMemo(() => getFixedOptions(...), [dataProvider, processedValue])`.
  const fixedOptionsWithMatching = useMemo(() => {
    if (dataProvider.type !== DATA_PROVIDER.FIXED) {
      return null;
    }

    return getFixedOptions(dataProvider, processedValue);
  }, [dataProvider, processedValue]);

  useEffect(() => {
    if (
      dataProvider.type !== DATA_PROVIDER.FIXED ||
      fixedOptionsWithMatching === null
    ) {
      return;
    }

    const validatedOptions = validateOptions(fixedOptionsWithMatching);

    setOptions(validatedOptions);
    const validSelectedOptions = validateSelectedOptions(
      validatedOptions.filter((option: MenuInputOption) => option.isChecked)
    );

    setSelectedOptions(validSelectedOptions);
    setIsLoading(false);
  }, [dataProvider.type, fixedOptionsWithMatching]);

  const placeholderText = getFieldPlaceholder(
    dataProvider,
    formValues,
    placeholder,
    initialData
  );

  const prevDependencyValues = useRef<string>('');
  const dependenciesChangedRef = useRef<boolean>(false); // Store dependenciesChanged before updating prevDependencyValues
  const isClearingRef = useRef<boolean>(false);
  const prevOptionsRef = useRef<string>('');
  const originalSavedValueRef = useRef<MenuInputOption[] | null>(null);
  const hasStoredOriginalValueRef = useRef<boolean>(false);
  const isInitialMount = useRef(true);
  const isLoadInFlightRef = useRef(false);
  const shouldResetSearchRef = useRef<boolean>(false); // Track when cascading requires immediate search reset
  const skipNextSearchLoadRef = useRef<boolean>(false); // Skip search effect when cascading already handled the load
  // Track API event ID for MultiSelect
  const apiEventIdRef = useRef<string>('');
  // Track if this is the first load
  const isFirstLoadRef = useRef(true);
  // AbortController for cancelling in-flight search requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Reset internal state when value becomes null/empty (form cleared)
  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setSelectedOptions([]);
      setOptions((prev) => prev.map((opt) => ({ ...opt, isChecked: false })));
    }
  }, [value]);

  // Store original saved value BEFORE cascading can clear it
  // This ensures we can match saved values even after cascading clears the current value
  useEffect(() => {
    // Only store once when we first get a valid value, and don't overwrite if already stored
    if (!hasStoredOriginalValueRef.current && value) {
      const currentProcessed = processedValue;

      if (
        currentProcessed &&
        Array.isArray(currentProcessed) &&
        currentProcessed.length > 0
      ) {
        originalSavedValueRef.current = currentProcessed;
        hasStoredOriginalValueRef.current = true;
      }
    }
  }, [value, processedValue]);

  // Track only dependency field values, not entire formValues
  // This prevents API calls when unrelated fields change
  const relevantDependencyValues = useDependencyValues(
    dataProvider,
    formValues,
    initialData
  );

  // Clear field value when dependencies change (cascading)
  useEffect(() => {
    const didCascade = handleMultiSelectCascading(
      dataProvider,
      formValues,
      relevantDependencyValues,
      prevDependencyValues.current,
      value,
      isClearingRef,
      onChange,
      initialData
    );

    // Reset search text and clear saved value refs if cascading occurred
    if (didCascade) {
      originalSavedValueRef.current = null; // Clear saved value to prevent stale matching
      hasStoredOriginalValueRef.current = true; // Mark as stored to prevent re-storing
      setSearchText('');
      shouldResetSearchRef.current = true; // Signal immediate reset needed
      skipNextSearchLoadRef.current = true; // Skip search effect when debouncedSearchText catches up
    }
    // Only depend on relevantDependencyValues - dataProvider doesn't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantDependencyValues]);

  // Cleanup: abort any in-flight request on unmount
  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  // Fallback: if loading persists beyond API timeout, show error
  useEffect(() => {
    if (!isLoading || loadError) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (isLoading && !loadError) {
        setIsLoading(false);
        setLoadError('Unable to load options');
      }
    }, 12000); // Slightly longer than API_TIMEOUT_IN_MS (10s)

    return () => clearTimeout(timer);
  }, [isLoading, loadError]);

  // Wrap getDataProviderPreview to add tracking and abort support
  const trackedGetDataProviderPreview = useMemo(
    () =>
      (
        payload: PreviewDataProviderPayload,
        callbackOptions?: {
          onSuccess?: (data: ApiResponse) => void;
          onError?: (error: ApiError) => void;
        },
        dependenciesChangedOverride?: boolean,
        signal?: AbortSignal
      ): void => {
        // Use override if provided, otherwise use the ref value (set before prevDependencyValues was updated)
        const dependenciesChanged =
          dependenciesChangedOverride !== undefined
            ? dependenciesChangedOverride
            : dependenciesChangedRef.current;
        const isFirstLoad = isFirstLoadRef.current;
        const dataProviderHasDependencies = hasDependencies(dataProvider);
        const currentPage = payload.page || 0;

        // Determine trigger type
        const triggerType = determineTriggerType(
          currentPage,
          dataProviderHasDependencies,
          dependenciesChanged,
          isFirstLoad
        );

        // Prepare API tracking data
        const trackingData = prepareApiTrackingData(
          field.id,
          field.fieldName,
          dataProvider,
          triggerType,
          currentPage,
          payload.dependencies,
          canvasFields,
          {
            isReadOnly,
            isDisabled: isReadOnly,
            isRequired,
          },
          payload.urlSlug
        );

        const eventId = trackApiTriggered({
          ...trackingData,
          startTime: Date.now(),
          status: 'pending',
        });

        apiEventIdRef.current = eventId;

        // Wrap callbacks to add tracking
        const wrappedCallbacks = {
          onSuccess: (data: ApiResponse) => {
            setLoadError(null);

            if (eventId) {
              if (data.error || data.errorMessage) {
                trackApiError(eventId, {
                  fieldId: field.id,
                  fieldName: field.fieldName,
                  error: {
                    type: 'api',
                    message: data.errorMessage || 'Unknown error',
                  },
                });
              } else {
                trackApiSuccess(eventId, {
                  fieldId: field.id,
                  fieldName: field.fieldName,
                  triggerType,
                  apiResponse: {
                    statusCode: 200,
                    dataCount: data.data?.data?.data?.length || 0,
                    hasNext: data.data?.hasNext || false,
                    totalPages: data.data?.data?.totalPages,
                  },
                });
              }
            }

            callbackOptions?.onSuccess?.(data);
          },
          onError: (apiError: ApiError) => {
            setLoadError('Unable to load options');

            if (eventId) {
              trackApiError(eventId, {
                fieldId: field.id,
                fieldName: field.fieldName,
                error: {
                  type: 'network',
                  message: apiError.message || 'Unknown error',
                },
              });
            }

            callbackOptions?.onError?.(apiError);
          },
        };

        // Call getDataProviderPreview with optional signal
        // Type assertion to ensure correct overload is used
        (
          getDataProviderPreview as (
            payload: PreviewDataProviderPayload,
            callbacks?: {
              onSuccess?: (data: ApiResponse) => void;
              onError?: (error: ApiError) => void;
            },
            signal?: AbortSignal
          ) => Promise<void>
        )(payload, wrappedCallbacks, signal).catch(() => {
          // Error is already handled by onError callback
        });
      },
    // Minimal dependencies - refs don't need to be listed, and we intentionally
    // exclude relevantDependencyValues to prevent recreation on dependency changes
    [
      dataProvider,
      field.id,
      field.fieldName,
      getDataProviderPreview,
      trackApiTriggered,
      trackApiSuccess,
      trackApiError,
      canvasFields,
      isReadOnly,
      isRequired,
    ]
  );

  const loadMoreOptions = useMemo(
    () =>
      createLoadMoreOptions(
        dataProvider,
        formValues,
        field.id,
        canvasFields ?? [],
        dataProviders ?? {},
        trackedGetDataProviderPreview,
        setIsLoading,
        setIsLoadingMore,
        setOptions,
        setHasMoreOptions,
        processedValue,
        paginationEnabled,
        setSelectedOptions,
        initialData,
        urlSlug,
        debouncedSearchText,
        dataProviderValuesToSearch,
        fieldTypeMap
      ),
    [
      dataProvider,
      formValues,
      field.id,
      canvasFields,
      dataProviders,
      trackedGetDataProviderPreview,
      processedValue,
      paginationEnabled,
      initialData,
      urlSlug,
      debouncedSearchText,
      dataProviderValuesToSearch,
    ]
  );

  const lastValuesToSearchWireKeyRef = useRef<string>('');

  // After entity `/load`, `valuesToSearch` becomes attachable when Formik matches the
  // Redux snapshot — refetch page 0 so the request includes saved codes (main load
  // effect intentionally does not depend on `processedValue` / wire).
  useEffect(() => {
    if (
      dataProvider.type !== DATA_PROVIDER.HTTP &&
      dataProvider.type !== DATA_PROVIDER.SQL &&
      dataProvider.type !== DATA_PROVIDER.DATASTORE
    ) {
      return;
    }

    if (isPublicForm || !dependenciesSatisfiedForProvider) {
      return;
    }

    if (!shouldAttachValuesToSearch(dataProviderValuesToSearch)) {
      lastValuesToSearchWireKeyRef.current = '';

      return;
    }

    const wireKey = JSON.stringify(dataProviderValuesToSearch);

    if (lastValuesToSearchWireKeyRef.current === wireKey) {
      return;
    }

    lastValuesToSearchWireKeyRef.current = wireKey;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();

    abortControllerRef.current = controller;
    loadMoreOptions(0, true, controller.signal);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- targeted refetch when wire payload appears
  }, [
    dataProvider.type,
    isPublicForm,
    dependenciesSatisfiedForProvider,
    dataProviderValuesToSearch,
    loadMoreOptions,
  ]);

  // Create unique dataTestId per field instance to prevent cross-component interference
  const uniqueDataTestId = `${testIds.FF_PREVIEW_MULTI_SELECT_FIELD}-${field.id}`;

  const { handleExpandedChange: handleInfiniteScroll } = useInfiniteScroll({
    dataTestId: uniqueDataTestId,
    hasMoreOptions,
    isLoading,
    isLoadingMore,
    optionsCount: options.length,
    onLoadMore: () => {
      const nextPage = page + 1;

      setPage(nextPage);
      loadMoreOptions(nextPage, hasMoreOptions);
    },
  });

  // Main effect to load options based on data provider type
  useEffect(() => {
    const dependenciesSatisfied = dependenciesSatisfiedForProvider;
    // Calculate dependenciesChanged BEFORE updating prevDependencyValues
    const dependenciesChanged =
      prevDependencyValues.current !== relevantDependencyValues;

    // Store in ref so trackedGetDataProviderPreview can use it
    dependenciesChangedRef.current = dependenciesChanged;

    const shouldLoad = shouldLoadOptions(
      dependenciesSatisfied,
      dependenciesChanged,
      options.length
    );

    if (!dependenciesSatisfied) {
      isLoadInFlightRef.current = false;
      setOptions([]);
      setSelectedOptions([]);
      setIsLoading(false);
      prevDependencyValues.current = relevantDependencyValues;
      prevOptionsRef.current = '';

      return;
    }

    if (dependenciesChanged) {
      isLoadInFlightRef.current = false;
    }

    if (shouldLoad && !isLoadInFlightRef.current) {
      if (dependenciesChanged) {
        prevDependencyValues.current = relevantDependencyValues;
      }

      isFirstLoadRef.current = false;

      try {
        resetOptionsLoadingState(
          setOptions,
          setSelectedOptions,
          setIsLoading,
          setPage,
          setHasMoreOptions,
          prevOptionsRef
        );

        if (
          dataProvider.type === DATA_PROVIDER.HTTP ||
          dataProvider.type === DATA_PROVIDER.SQL ||
          dataProvider.type === DATA_PROVIDER.DATASTORE
        ) {
          if (isPublicForm) {
            setOptions([]);
            setSelectedOptions([]);
            setIsLoading(false);
            setHasMoreOptions(false);

            return;
          }

          // Use empty search if we just cascaded, otherwise use debounced value
          const effectiveSearchText = shouldResetSearchRef.current
            ? ''
            : debouncedSearchText;

          shouldResetSearchRef.current = false; // Reset flag

          isLoadInFlightRef.current = true;

          const wrappedSetIsLoading = (loading: boolean) => {
            if (!loading) {
              isLoadInFlightRef.current = false;
            }

            setIsLoading(loading);
          };

          // Create a one-time loadMoreOptions with correct search text
          const loadOptions = createLoadMoreOptions(
            dataProvider,
            formValues,
            field.id,
            canvasFields ?? [],
            dataProviders ?? {},
            trackedGetDataProviderPreview,
            wrappedSetIsLoading,
            setIsLoadingMore,
            setOptions,
            setHasMoreOptions,
            processedValue,
            paginationEnabled,
            setSelectedOptions,
            initialData,
            urlSlug,
            effectiveSearchText,
            dataProviderValuesToSearch,
            fieldTypeMap
          );

          loadOptions(0, true);
        } else if (dataProvider.type === DATA_PROVIDER.FIXED) {
          const fixedOptions = getFixedOptions(dataProvider, processedValue);
          const validatedOptions = validateOptions(fixedOptions);

          setOptions(validatedOptions);
          // Validate selected options to ensure they have valid structure
          const validSelectedOptions = validateSelectedOptions(
            validatedOptions.filter(
              (option: MenuInputOption) => option.isChecked
            )
          );

          setSelectedOptions(validSelectedOptions);
          setIsLoading(false);
        }
      } catch {
        setOptions([]);
        setSelectedOptions([]);
        setIsLoading(false);
      }
    }
    // Minimal dependencies to prevent duplicate API calls:
    // - formValues excluded: we use relevantDependencyValues instead
    // - processedValue excluded: value changes shouldn't trigger reload
    // - trackedGetDataProviderPreview excluded: function is stable enough via refs
    // - debouncedSearchText excluded: handled by dedicated search effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataProvider,
    dependenciesSatisfiedForProvider,
    field.id,
    relevantDependencyValues,
    // Note: Other dependencies intentionally excluded to match SingleSelect pattern
  ]);

  // Trigger API call when search text changes
  useEffect(() => {
    // Skip on initial mount - main loading effect handles it
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }

    // Skip if cascading already triggered a load with empty search text
    // This prevents duplicate API calls when debouncedSearchText catches up to the reset
    if (skipNextSearchLoadRef.current && debouncedSearchText === '') {
      skipNextSearchLoadRef.current = false;

      return;
    }

    // Don't search if dependencies are not satisfied
    if (!dependenciesSatisfiedForProvider) {
      return;
    }

    if (
      dataProvider.type === DATA_PROVIDER.HTTP ||
      dataProvider.type === DATA_PROVIDER.SQL ||
      dataProvider.type === DATA_PROVIDER.DATASTORE
    ) {
      if (isPublicForm) {
        return;
      }

      // Cancel previous request when starting a new search
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const controller = new AbortController();

      abortControllerRef.current = controller;

      setPage(0);
      setHasMoreOptions(true);
      // Don't clear options or set isLoading during search — existing options
      // stay visible (DS filters them client-side) while the API loads,
      // preventing a flicker to "Loading options..." and back.

      // Create a one-time loadMoreOptions with abort signal support
      const searchLoadOptions = createLoadMoreOptions(
        dataProvider,
        formValues,
        field.id,
        canvasFields ?? [],
        dataProviders ?? {},
        trackedGetDataProviderPreview,
        setIsLoading,
        setIsLoadingMore,
        setOptions,
        setHasMoreOptions,
        processedValue,
        paginationEnabled,
        setSelectedOptions,
        initialData,
        urlSlug,
        debouncedSearchText,
        dataProviderValuesToSearch
      );

      searchLoadOptions(0, true, controller.signal);
    }
    // loadMoreOptions is intentionally excluded from deps to prevent triggering
    // when formValues change. We only want this effect to run when search text changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchText,
    dataProvider.type,
    dependenciesSatisfiedForProvider,
  ]);

  // Apply value matching when options are loaded. Skip FIXED: selection is driven
  // by `fixedOptionsWithMatching` + its effect; this effect would re-apply stale
  // `options` from the prior render and clear checks / selected chips.
  useEffect(() => {
    if (dataProvider.type === DATA_PROVIDER.FIXED) {
      return;
    }

    // Wait for loading to complete before matching
    if (isLoading) {
      return;
    }

    if (options.length === 0) {
      prevOptionsRef.current = '';

      return;
    }

    // Use original saved value if processedValue is null/empty (cleared by cascading)
    // Otherwise use processedValue (current value)
    // This ensures we can match saved values even after cascading clears the current value
    const valueToMatch =
      !processedValue ||
      (Array.isArray(processedValue) && processedValue.length === 0)
        ? originalSavedValueRef.current
        : processedValue;

    if (!valueToMatch) {
      return;
    }

    // Ensure valueToMatch is an array for MultiSelect
    const valueArray = Array.isArray(valueToMatch)
      ? valueToMatch
      : [valueToMatch];

    // Create a combined key to detect if either options or valueToMatch changed
    const combinedKey = createOptionsValueKey(options, valueArray);
    const hasChanged = prevOptionsRef.current !== combinedKey;

    if (!hasChanged) {
      return;
    }

    // Apply value matching and update selected options
    applyValueMatchingAndUpdateSelected(
      options,
      dataProvider,
      valueArray,
      setOptions,
      setSelectedOptions,
      paginationEnabled
    );

    // Clear the original saved value ref after successful matching
    // This prevents it from interfering with future user selections
    // Check selectedOptions state after a microtask to allow state updates
    Promise.resolve()
      .then(() => {
        if (selectedOptions.length > 0 && originalSavedValueRef.current) {
          originalSavedValueRef.current = null;
        }
      })
      .catch(() => {
        // Ignore errors
      });

    // Update ref with the new combined key
    prevOptionsRef.current = combinedKey;
  }, [processedValue, dataProvider.type, options, isLoading]);

  // Ensure options array is always valid (stable reference for HTTP/SQL providers)
  const validatedOptions = useMemo(() => validateOptions(options), [options]);

  const isPaginatedRemoteProvider =
    paginationEnabled &&
    (dataProvider.type === DATA_PROVIDER.HTTP ||
      dataProvider.type === DATA_PROVIDER.SQL);

  // For paginated remote lists, `options` is only one page — do not drop chips for
  // values that are selected but not on the currently loaded page.
  const validSelectedOptionsForRender = useMemo(() => {
    if (isPaginatedRemoteProvider) {
      return validateSelectedOptions(selectedOptions);
    }

    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }

    const validatedSelected = validateSelectedOptions(selectedOptions);

    return validatedSelected.filter((selected) =>
      validatedOptions.some((opt) => opt.value === selected.value)
    );
  }, [selectedOptions, options, validatedOptions, isPaginatedRemoteProvider]);

  // Filter by search text only for FIXED providers
  const validOptionsForRender = useMemo(() => {
    if (dataProvider.type === DATA_PROVIDER.FIXED && searchText) {
      return validatedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return validatedOptions;
  }, [validatedOptions, dataProvider.type, searchText]);

  const handleSearchChange = (searchValue: string) => {
    setSearchText(searchValue.trimStart());
  };

  const handleInputReset = () => {
    setSearchText('');
  };

  const handleExpandedChange = useCallback(
    (expanded: boolean) => {
      handleInfiniteScroll(expanded);

      if (!expanded && searchText) {
        setSearchText('');
      }
    },
    [handleInfiniteScroll, searchText]
  );

  return (
    <MenuInput
      isMulti
      inputSize='medium'
      options={validOptionsForRender}
      selectedOptions={validSelectedOptionsForRender}
      setSelectedOptions={(selected: MenuInputOption[]) => {
        onTouch?.();
        // Validate and filter selected options to ensure they have valid structure
        const validSelected = validateSelectedOptions(selected);

        // Track value selection
        if (
          dataProvider.dependencies &&
          Object.keys(dataProvider.dependencies).length > 0 &&
          validSelected.length > 0
        ) {
          const dependencies = buildDependencies(
            dataProvider,
            formValues,
            initialData,
            fieldTypeMap
          );

          trackValueSelected({
            fieldId: field.id,
            fieldName: field.fieldName,
            selectedValue: validSelected,
            dependencies,
          });
        }

        setOptions((curr) => {
          const validated = validateOptions(curr);

          return validated.map((opt) => ({
            ...opt,
            isChecked: validSelected.some((s) => s.value === opt.value),
          }));
        });
        setSelectedOptions(validSelected);
        onChange?.(validSelected);
      }}
      onChange={() => {}}
      label={label}
      inputLabel={
        <FieldLabel
          label={label}
          htmlFor={fieldName}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
        />
      }
      minOptionsForSearch={0}
      menuItemHeightConfigs={{ maxHeight: '200' }}
      placeholder={
        isLoading && options.length === 0 && !loadError
          ? l('Loading options...')
          : placeholderText
      }
      isRequired={isRequired}
      isCritical={!!(error || loadError)}
      isDisabled={isReadOnly}
      CriticalHelperText={
        <InlineMessage
          text={error || loadError || ''}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      }
      HelperText={
        helperText ? (
          <InlineMessage
            text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
            colorTheme='neutral'
            size='small'
          />
        ) : undefined
      }
      hasMoreOptions={hasMoreOptions}
      isLoading={
        (isLoadingMore || (isLoading && options.length === 0)) && !loadError
      }
      loadingText={l('Loading options...')}
      isSearchDisabled
      onSearchTermChange={handleSearchChange}
      onInputReset={handleInputReset}
      setIsExpanded={handleExpandedChange}
      // TODO: Pass isMobileWidth={isMobileXlargeWidth} once the DS MultiSelect component supports it
      // Modals need the overflow to not be hidden. Pages do need the overflow to be hidden.
      isPortalEnabled={isModalForm}
      shouldMatchTriggerWidth
      className={styles.multiSelect}
      classNames={{
        dropdownClassName: styles.multiSelectDropdown,
        inputFieldClassName: cx(isReadOnly && styles.value__isReadOnly),
      }}
      dataTestId={uniqueDataTestId}
    />
  );
}

export default MultiSelect;
